import { ModalConfirm } from '@faceup/ui-base'
import { Component, type ReactNode, useContext } from 'react'
import { UiContext } from '../UiProvider/UiProvider'

type ModalErrorBoundaryProps = {
  children: ReactNode
}

type ModalErrorBoundaryState = {
  hasError: boolean
  dismissed: boolean
}

/**
 * Catches render/query errors thrown by a modal's content so one failing widget can't take down the
 * whole page. On error, it shows a small "something went wrong" modal instead of letting the error
 * bubble to the app-level boundary and blank the page. The wrapped subtree unmounts when the modal
 * closes, so the next open retries with a clean state.
 *
 * Background: in kredenc a single report category with no translations used to crash the entire
 * institution detail page when opening the "Add member" modal (ENG-1528).
 */
export class ModalErrorBoundary extends Component<
  ModalErrorBoundaryProps,
  ModalErrorBoundaryState
> {
  state: ModalErrorBoundaryState = { hasError: false, dismissed: false }

  static getDerivedStateFromError(): Partial<ModalErrorBoundaryState> {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    // Re-throw out of band so the error still reaches global error tracking (Sentry's default
    // global handler picks up window.onerror) without bubbling back through React and blanking the
    // page - and without pulling a Sentry dependency into this UI package.
    setTimeout(() => {
      throw error
    })
  }

  private handleClose = () => {
    this.setState({ dismissed: true })
  }

  render() {
    const { hasError, dismissed } = this.state
    if (hasError) {
      return <ModalErrorFallback open={!dismissed} onClose={this.handleClose} />
    }
    return this.props.children
  }
}

type ModalErrorFallbackProps = {
  open: boolean
  onClose: () => void
}

// Split out so it can read the app-provided translation from context (a class component cannot use
// hooks); the confirm/cancel button labels come from the ui-base locale automatically.
const ModalErrorFallback = ({ open, onClose }: ModalErrorFallbackProps) => {
  const { modalError } = useContext(UiContext)
  return (
    <ModalConfirm open={open} onCancel={onClose} onConfirm={onClose}>
      {modalError?.text ?? 'Something went wrong, please try again later.'}
    </ModalConfirm>
  )
}
